import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import DisclosureTable from "./DisclosureTable.js";
import ScrollToTop from "./ScrollToTop.js";
import "../styles/MandatoryPublicDisclosure.css";
import { auth, db, storage } from "../config/firebaseConfig";
import { getUserRoles } from "../utils/roleAccess";
import {
  general,
  documents,
  academics,
  staff,
  infra,
} from "../data/disclosureData.js";

const H3 = ["S.NO", "DOCUMENTS/INFORMATION", "DETAILS"];
const DOCUMENTS_COLLECTION = "mandatoryPublicDisclosureDocuments";
const CONTENT_COLLECTION = "mandatoryPublicDisclosureContent";
const CONTENT_DOCUMENT_ID = "content";
const CUSTOM_SECTION_PREFIX = "custom-section-";
const CONTENT_KEYS_BY_SECTION_ID = {
  "general-information": "general",
  "documents-information": "documents",
  "results-academics": "academics",
  "teaching-staff": "staff",
  "school-infrastructure": "infra",
};
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const DEFAULT_CONTENT = { general, documents, academics, staff, infra };

function getFirebaseErrorMessage(error, action) {
  switch (error?.code) {
    case "permission-denied":
      return `Firebase rejected the ${action}. Your account does not have permission to change disclosure data.`;
    case "unauthenticated":
      return `Firebase rejected the ${action}. Please sign in again and retry.`;
    case "unavailable":
      return `Firebase is temporarily unavailable, so the ${action} was not saved. Please retry.`;
    case "failed-precondition":
      return `Firebase could not complete the ${action}. Check the Firestore database and its security rules.`;
    default:
      return error?.message || `Unable to ${action}. Please try again.`;
  }
}

function addDocumentUrls(rows, uploadedDocuments) {
  return (Array.isArray(rows) ? rows : []).map((row) =>
    row.map((cell) => {
      if (!cell?.documentId) return cell;
      return { ...cell, doc: uploadedDocuments[cell.documentId]?.url || "" };
    }),
  );
}

function storageFileName(fileName) {
  return String(fileName || "document")
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-");
}

function customDocumentId(key) {
  const safeKey = String(key || "document")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `custom-${safeKey || "document"}-${Date.now()}`;
}

// Firestore does not support an array directly inside another array.  The UI
// uses arrays for table rows, so store each row as a map with a `cells` array.
function rowsForFirestore(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    cells: removeResolvedDocumentUrls(row),
  }));
}

function rowsFromFirestore(rows, fallback = []) {
  if (!Array.isArray(rows)) return fallback;

  return rows.map((row) => {
    // Keep this fallback for data that may have been read before this format
    // was introduced.
    if (Array.isArray(row)) return row;
    return Array.isArray(row?.cells) ? row.cells : [];
  });
}

function customSectionsForFirestore(sections) {
  return (Array.isArray(sections) ? sections : []).map((section) => ({
    id: section.id,
    title: section.title,
    rows: rowsForFirestore(section.rows),
  }));
}

function contentFromFirestore(data = {}) {
  return Object.entries(CONTENT_KEYS_BY_SECTION_ID).reduce(
    (content, [sectionId, contentKey]) => ({
      ...content,
      [contentKey]: rowsFromFirestore(
        data[contentKey] ?? data[sectionId],
        DEFAULT_CONTENT[contentKey],
      ),
    }),
    {},
  );
}

function customSectionsFromFirestore(data = {}) {
  if (!Array.isArray(data.customSections)) return [];

  return data.customSections
    .filter(
      (section) =>
        section &&
        typeof section.id === "string" &&
        typeof section.title === "string" &&
        Array.isArray(section.rows),
    )
    .map((section) => ({
      ...section,
      rows: rowsFromFirestore(section.rows),
    }));
}

function removeResolvedDocumentUrls(row) {
  return (Array.isArray(row) ? row : []).map((cell) => {
    if (cell?.documentId) return { documentId: cell.documentId };
    if (cell?.mail !== undefined) return { mail: String(cell.mail || "") };
    if (cell?.video !== undefined) return { video: String(cell.video || "") };
    return cell === undefined ? "" : cell;
  });
}

export default function MandatoryPublicDisclosure() {
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [disclosureContent, setDisclosureContent] = useState(DEFAULT_CONTENT);
  const [customSections, setCustomSections] = useState([]);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [canManageDocuments, setCanManageDocuments] = useState(false);
  const [uploadingDocumentId, setUploadingDocumentId] = useState("");
  const [message, setMessage] = useState("");
  const [actionSection, setActionSection] = useState("");
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [addingSection, setAddingSection] = useState(false);

  useEffect(() => {
    const handleReadError = (error) => {
      console.error("Unable to load disclosure data:", error);
      setMessage(
        error.code === "permission-denied"
          ? "You do not have permission to read disclosure data."
          : error.message ||
              "Disclosure data is temporarily unavailable. Please try again later.",
      );
    };

    const unsubscribeDocuments = onSnapshot(
      collection(db, DOCUMENTS_COLLECTION),
      (snapshot) => {
        setUploadedDocuments(
          snapshot.docs.reduce(
            (documentsById, documentSnapshot) => ({
              ...documentsById,
              [documentSnapshot.id]: documentSnapshot.data(),
            }),
            {},
          ),
        );
      },
      handleReadError,
    );

    const unsubscribeContent = onSnapshot(
      doc(db, CONTENT_COLLECTION, CONTENT_DOCUMENT_ID),
      (snapshot) => {
        if (!snapshot.exists()) {
          setDisclosureContent(DEFAULT_CONTENT);
          setCustomSections([]);
          setHiddenSections([]);
          return;
        }

        const contentData = snapshot.data();
        setDisclosureContent(contentFromFirestore(contentData));
        setCustomSections(customSectionsFromFirestore(contentData));
        setHiddenSections(
          Array.isArray(contentData.hiddenSections)
            ? contentData.hiddenSections.filter(
                (sectionId) => typeof sectionId === "string",
              )
            : [],
        );
      },
      handleReadError,
    );

    return () => {
      unsubscribeDocuments();
      unsubscribeContent();
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCanManageDocuments(false);
        return;
      }

      try {
        const userSnapshot = await getDoc(doc(db, "users", user.uid));
        const roles = userSnapshot.exists()
          ? getUserRoles(userSnapshot.data())
          : [];
        setCanManageDocuments(roles.includes("admin") || roles.includes("it"));
      } catch (error) {
        console.error("Unable to check disclosure management access:", error);
        setCanManageDocuments(false);
      }
    });

    return unsubscribe;
  }, []);

  const documentRows = useMemo(
    () => addDocumentUrls(disclosureContent.documents, uploadedDocuments),
    [disclosureContent.documents, uploadedDocuments],
  );
  const generalRows = useMemo(
    () => addDocumentUrls(disclosureContent.general, uploadedDocuments),
    [disclosureContent.general, uploadedDocuments],
  );
  const academicRows = useMemo(
    () => addDocumentUrls(disclosureContent.academics, uploadedDocuments),
    [disclosureContent.academics, uploadedDocuments],
  );
  const staffRows = useMemo(
    () => addDocumentUrls(disclosureContent.staff, uploadedDocuments),
    [disclosureContent.staff, uploadedDocuments],
  );
  const infrastructureRows = useMemo(
    () => addDocumentUrls(disclosureContent.infra, uploadedDocuments),
    [disclosureContent.infra, uploadedDocuments],
  );

  const isCustomSection = (sectionId) =>
    sectionId.startsWith(CUSTOM_SECTION_PREFIX);
  const getCustomSectionId = (sectionId) =>
    sectionId.slice(CUSTOM_SECTION_PREFIX.length);
  const getContentKey = (sectionId) =>
    CONTENT_KEYS_BY_SECTION_ID[sectionId] || sectionId;
  const statusMessageForSection = (sectionId) =>
    actionSection === sectionId && !message.includes("successfully")
      ? message
      : "";

  const removeDocumentFile = async (row) => {
    const documentCell = row.find((cell) => cell?.documentId);
    if (!documentCell) return;

    const documentData = uploadedDocuments[documentCell.documentId];
    try {
      if (documentData?.storagePath) {
        await deleteObject(ref(storage, documentData.storagePath));
      }
      await deleteDoc(doc(db, DOCUMENTS_COLLECTION, documentCell.documentId));
      setUploadedDocuments((current) => {
        const nextDocuments = { ...current };
        delete nextDocuments[documentCell.documentId];
        return nextDocuments;
      });
    } catch (error) {
      console.warn("Unable to remove the document file:", error);
    }
  };

  const saveContentRow = async (sectionId, rowIndex, row) => {
    setActionSection(sectionId);
    if (!auth.currentUser) {
      setMessage("Please sign in again before saving disclosure text.");
      return false;
    }

    if (isCustomSection(sectionId)) {
      const customSectionId = getCustomSectionId(sectionId);
      const previousSections = customSections;
      const nextSections = customSections.map((section) =>
        section.id === customSectionId
          ? {
              ...section,
              rows: section.rows.map((currentRow, index) =>
                index === rowIndex
                  ? removeResolvedDocumentUrls(row)
                  : currentRow,
              ),
            }
          : section,
      );
      setCustomSections(nextSections);
      setMessage("");

      try {
        await setDoc(
          doc(db, CONTENT_COLLECTION, CONTENT_DOCUMENT_ID),
          {
            customSections: customSectionsForFirestore(nextSections),
            updatedAt: serverTimestamp(),
            updatedBy: auth.currentUser?.uid || "",
          },
          { merge: true },
        );
        setMessage("Text updated successfully.");
        return true;
      } catch (error) {
        console.error("Unable to save custom disclosure text:", error);
        setCustomSections(previousSections);
        setMessage(getFirebaseErrorMessage(error, "row update"));
        return false;
      }
    }

    const contentKey = getContentKey(sectionId);
    const previousContent = disclosureContent;
    const currentRows = Array.isArray(disclosureContent[contentKey])
      ? disclosureContent[contentKey]
      : DEFAULT_CONTENT[contentKey] || [];
    const updatedRows = currentRows.map((currentRow, index) =>
      index === rowIndex ? removeResolvedDocumentUrls(row) : currentRow,
    );
    const nextContent = { ...disclosureContent, [contentKey]: updatedRows };
    setDisclosureContent(nextContent);
    setMessage("");

    try {
      await setDoc(
        doc(db, CONTENT_COLLECTION, CONTENT_DOCUMENT_ID),
        {
          [contentKey]: rowsForFirestore(updatedRows),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || "",
        },
        { merge: true },
      );
      setMessage("Text updated successfully.");
      return true;
    } catch (error) {
      console.error("Unable to save disclosure text:", error);
      setDisclosureContent(previousContent);
      setMessage(getFirebaseErrorMessage(error, "row update"));
      return false;
    }
  };

  const addContentRow = async (sectionId, { key, value, file }) => {
    setActionSection(sectionId);
    if (!auth.currentUser) {
      setMessage("Please sign in again before adding a disclosure row.");
      return false;
    }
    if (!key) {
      setMessage("Enter a key or row name before adding a row.");
      return false;
    }
    if (file?.size > MAX_FILE_SIZE) {
      setMessage("Choose a file smaller than 20 MB.");
      return false;
    }

    const customSectionId = isCustomSection(sectionId)
      ? getCustomSectionId(sectionId)
      : "";
    const customSection = customSections.find(
      (section) => section.id === customSectionId,
    );
    const contentKey = getContentKey(sectionId);
    const currentRows = customSection
      ? customSection.rows
      : Array.isArray(disclosureContent[contentKey])
        ? disclosureContent[contentKey]
        : DEFAULT_CONTENT[contentKey] || [];
    const numericRows = currentRows
      .map((row) => Number(row[0]))
      .filter((number) => Number.isFinite(number));
    const nextSerial = String(Math.max(0, ...numericRows) + 1);
    let documentId = "";
    let documentData = null;
    setMessage("");

    try {
      if (file) {
        documentId = customDocumentId(key);
        const path = `mandatory-public-disclosure/custom/${documentId}/${storageFileName(file.name)}`;
        const fileReference = ref(storage, path);
        await uploadBytes(fileReference, file, {
          contentType: file.type || "application/octet-stream",
        });
        const url = await getDownloadURL(fileReference);
        documentData = {
          url,
          storagePath: path,
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          fileSize: file.size,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || "",
        };
        await setDoc(doc(db, DOCUMENTS_COLLECTION, documentId), documentData);
      }

      const newRow = [nextSerial, key, documentId ? { documentId } : value];
      const updatedRows = [...currentRows, newRow];
      const nextSections = customSection
        ? customSections.map((section) =>
            section.id === customSectionId
              ? { ...section, rows: updatedRows }
              : section,
          )
        : customSections;
      await setDoc(
        doc(db, CONTENT_COLLECTION, CONTENT_DOCUMENT_ID),
        {
          ...(customSection
            ? { customSections: customSectionsForFirestore(nextSections) }
            : { [contentKey]: rowsForFirestore(updatedRows) }),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || "",
        },
        { merge: true },
      );
      if (customSection) {
        setCustomSections(nextSections);
      } else {
        setDisclosureContent((current) => ({
          ...current,
          [contentKey]: updatedRows,
        }));
      }
      if (documentId) {
        setUploadedDocuments((current) => ({
          ...current,
          [documentId]: documentData,
        }));
      }
      setMessage(
        file
          ? "New row and file added successfully."
          : "New row added successfully.",
      );
      return true;
    } catch (error) {
      console.error("Unable to add disclosure row:", error);
      setMessage(getFirebaseErrorMessage(error, "new row"));
      return false;
    }
  };

  const addCustomSection = async (title) => {
    const trimmedTitle = String(title || "").trim();
    if (!trimmedTitle) return false;

    const nextSections = [
      ...customSections,
      {
        id: `section-${Date.now()}`,
        title: trimmedTitle,
        rows: [],
      },
    ];

    try {
      await setDoc(
        doc(db, CONTENT_COLLECTION, CONTENT_DOCUMENT_ID),
        {
          customSections: customSectionsForFirestore(nextSections),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || "",
        },
        { merge: true },
      );
      setCustomSections(nextSections);
      setMessage("New section added successfully.");
      return true;
    } catch (error) {
      console.error("Unable to add disclosure section:", error);
      setMessage(
        error.message || "Unable to add the section. Please try again.",
      );
      return false;
    }
  };

  const handleAddCustomSection = async () => {
    if (!newSectionTitle.trim()) {
      setMessage("Enter a section title before adding a section.");
      return;
    }
    setAddingSection(true);
    const didAdd = await addCustomSection(newSectionTitle);
    setAddingSection(false);
    if (didAdd) setNewSectionTitle("");
  };

  const deleteContentRow = async (sectionId, rowIndex) => {
    const customSectionId = isCustomSection(sectionId)
      ? getCustomSectionId(sectionId)
      : "";
    const customSection = customSections.find(
      (section) => section.id === customSectionId,
    );
    const contentKey = getContentKey(sectionId);
    const currentRows = customSection
      ? customSection.rows
      : Array.isArray(disclosureContent[contentKey])
        ? disclosureContent[contentKey]
        : DEFAULT_CONTENT[contentKey] || [];
    const removedRow = currentRows[rowIndex];
    if (!removedRow) return false;

    const updatedRows = currentRows.filter((_, index) => index !== rowIndex);
    const nextSections = customSection
      ? customSections.map((section) =>
          section.id === customSectionId
            ? { ...section, rows: updatedRows }
            : section,
        )
      : customSections;

    try {
      await setDoc(
        doc(db, CONTENT_COLLECTION, CONTENT_DOCUMENT_ID),
        {
          ...(customSection
            ? { customSections: customSectionsForFirestore(nextSections) }
            : { [contentKey]: rowsForFirestore(updatedRows) }),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || "",
        },
        { merge: true },
      );
      if (customSection) {
        setCustomSections(nextSections);
      } else {
        setDisclosureContent((current) => ({
          ...current,
          [contentKey]: updatedRows,
        }));
      }
      await removeDocumentFile(removedRow);
      setMessage("Row removed successfully.");
      return true;
    } catch (error) {
      console.error("Unable to remove disclosure row:", error);
      setMessage(
        error.message || "Unable to remove the row. Please try again.",
      );
      return false;
    }
  };

  const deleteDisclosureSection = async (sectionId) => {
    if (isCustomSection(sectionId)) {
      const customSectionId = getCustomSectionId(sectionId);
      const removedSection = customSections.find(
        (section) => section.id === customSectionId,
      );
      const nextSections = customSections.filter(
        (section) => section.id !== customSectionId,
      );

      try {
        await setDoc(
          doc(db, CONTENT_COLLECTION, CONTENT_DOCUMENT_ID),
          {
            customSections: customSectionsForFirestore(nextSections),
            updatedAt: serverTimestamp(),
            updatedBy: auth.currentUser?.uid || "",
          },
          { merge: true },
        );
        setCustomSections(nextSections);
        await Promise.all((removedSection?.rows || []).map(removeDocumentFile));
        setMessage("Custom section removed successfully.");
        return true;
      } catch (error) {
        console.error("Unable to remove custom disclosure section:", error);
        setMessage(
          error.message || "Unable to remove the section. Please try again.",
        );
        return false;
      }
    }

    if (hiddenSections.includes(sectionId)) return true;
    const nextHiddenSections = [...hiddenSections, sectionId];

    try {
      await setDoc(
        doc(db, CONTENT_COLLECTION, CONTENT_DOCUMENT_ID),
        {
          hiddenSections: nextHiddenSections,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || "",
        },
        { merge: true },
      );
      setHiddenSections(nextHiddenSections);
      setMessage("Section removed from the public page.");
      return true;
    } catch (error) {
      console.error("Unable to hide disclosure section:", error);
      setMessage(
        error.message || "Unable to remove the section. Please try again.",
      );
      return false;
    }
  };

  const handleDocumentUpload = async (documentId, file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setMessage("Choose a file smaller than 20 MB.");
      return;
    }

    const currentDocument = uploadedDocuments[documentId];
    const path = `mandatory-public-disclosure/${documentId}/${Date.now()}_${storageFileName(file.name)}`;
    setUploadingDocumentId(documentId);
    setMessage("");

    try {
      const fileReference = ref(storage, path);
      await uploadBytes(fileReference, file, {
        contentType: file.type || "application/octet-stream",
      });
      const url = await getDownloadURL(fileReference);
      const documentData = {
        url,
        storagePath: path,
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        fileSize: file.size,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || "",
      };

      await setDoc(doc(db, DOCUMENTS_COLLECTION, documentId), documentData, {
        merge: true,
      });
      setUploadedDocuments((current) => ({
        ...current,
        [documentId]: { ...current[documentId], ...documentData },
      }));

      if (
        currentDocument?.storagePath &&
        currentDocument.storagePath !== path
      ) {
        deleteObject(ref(storage, currentDocument.storagePath)).catch((error) =>
          console.warn("Unable to remove replaced disclosure document:", error),
        );
      }
      setMessage("Document uploaded successfully.");
    } catch (error) {
      console.error("Unable to upload disclosure document:", error);
      setMessage(
        error.message || "Unable to upload the document. Please try again.",
      );
    } finally {
      setUploadingDocumentId("");
    }
  };

  return (
    <main className="disclosure-page">
      <ScrollToTop />
      <header className="disclosure-hero">
        <div className="disclosure-hero-content">
          <nav className="disclosure-breadcrumb" aria-label="Breadcrumb">
            <a href="/">Home</a>
            <span aria-hidden="true">/</span>
            <span>Mandatory Public Disclosure</span>
          </nav>
          <p className="disclosure-eyebrow">School information centre</p>
          <h1>Mandatory Public Disclosure</h1>
          <p className="disclosure-hero-copy">
            Clear, current information about our school, academics, people,
            facilities, and statutory documents.
          </p>
          <div
            className="disclosure-stats"
            aria-label="Disclosure page summary"
          >
            <div className="disclosure-stat">
              <strong>5</strong>
              <span>Information areas</span>
            </div>
            <div className="disclosure-stat">
              <strong>14+</strong>
              <span>Published records</span>
            </div>
            <div className="disclosure-stat">
              <strong>2026</strong>
              <span>Academic year</span>
            </div>
          </div>
        </div>
      </header>
      <div className="disclosure-content">
        <div className="disclosure-intro">
          <div>
            <p className="disclosure-eyebrow">At a glance</p>
            <h2>Know about Rising Stars Public School</h2>
          </div>
          <p>
            Browse the sections below to find essential school details and
            download supporting documents directly from the source.
          </p>
        </div>
        {canManageDocuments && (
          <>
            <p className="disclosure-management-note">
              You are signed in with document-management access. Use Edit to
              change table text, and Upload or Replace to manage document files.
            </p>
            <div className="disclosure-add-section">
              <label htmlFor="new-disclosure-section">Add new section</label>
              <div className="disclosure-add-section-fields">
                <input
                  id="new-disclosure-section"
                  className="disclosure-text-input"
                  value={newSectionTitle}
                  placeholder="Section title"
                  onChange={(event) => setNewSectionTitle(event.target.value)}
                />
                <button
                  type="button"
                  className="disclosure-edit-button"
                  disabled={addingSection}
                  onClick={handleAddCustomSection}
                >
                  {addingSection ? "Adding..." : "Add section"}
                </button>
              </div>
            </div>
          </>
        )}
        {message && (
          <p className="disclosure-status" role="status">
            {message}
          </p>
        )}
        {!hiddenSections.includes("general-information") && (
          <DisclosureTable
            sectionId="general-information"
            title="A-GENERAL INFORMATION"
            rows={generalRows}
            canManageDocuments={canManageDocuments}
            uploadingDocumentId={uploadingDocumentId}
            onDocumentUpload={handleDocumentUpload}
            canManageContent={canManageDocuments}
            onSaveRow={saveContentRow}
            onAddRow={addContentRow}
            statusMessage={statusMessageForSection("general-information")}
            onDeleteRow={deleteContentRow}
            onDeleteSection={deleteDisclosureSection}
          />
        )}
        {!hiddenSections.includes("documents-information") && (
          <DisclosureTable
            sectionId="documents-information"
            title="B-DOCUMENTS AND INFORMATION"
            head={H3}
            rows={documentRows}
            canManageDocuments={canManageDocuments}
            uploadingDocumentId={uploadingDocumentId}
            onDocumentUpload={handleDocumentUpload}
            canManageContent={canManageDocuments}
            onSaveRow={saveContentRow}
            onAddRow={addContentRow}
            statusMessage={statusMessageForSection("documents-information")}
            onDeleteRow={deleteContentRow}
            onDeleteSection={deleteDisclosureSection}
          />
        )}
        {!hiddenSections.includes("results-academics") && (
          <DisclosureTable
            sectionId="results-academics"
            title="C- RESULT AND ACADEMICS"
            head={H3}
            rows={academicRows}
            canManageDocuments={canManageDocuments}
            uploadingDocumentId={uploadingDocumentId}
            onDocumentUpload={handleDocumentUpload}
            canManageContent={canManageDocuments}
            onSaveRow={saveContentRow}
            onAddRow={addContentRow}
            statusMessage={statusMessageForSection("results-academics")}
            onDeleteRow={deleteContentRow}
            onDeleteSection={deleteDisclosureSection}
          />
        )}
        {!hiddenSections.includes("teaching-staff") && (
          <DisclosureTable
            sectionId="teaching-staff"
            title="D- STAFF (TEACHING)"
            head={["S.NO", "INFORMATION", "DETAILS"]}
            rows={staffRows}
            canManageDocuments={canManageDocuments}
            uploadingDocumentId={uploadingDocumentId}
            onDocumentUpload={handleDocumentUpload}
            canManageContent={canManageDocuments}
            onSaveRow={saveContentRow}
            onAddRow={addContentRow}
            statusMessage={statusMessageForSection("teaching-staff")}
            onDeleteRow={deleteContentRow}
            onDeleteSection={deleteDisclosureSection}
          />
        )}
        {!hiddenSections.includes("school-infrastructure") && (
          <DisclosureTable
            sectionId="school-infrastructure"
            title="E- SCHOOL INFRASTRUCTURE"
            head={["S.NO", "INFORMATION", "DETAILS"]}
            rows={infrastructureRows}
            canManageDocuments={canManageDocuments}
            uploadingDocumentId={uploadingDocumentId}
            onDocumentUpload={handleDocumentUpload}
            canManageContent={canManageDocuments}
            onSaveRow={saveContentRow}
            onAddRow={addContentRow}
            statusMessage={statusMessageForSection("school-infrastructure")}
            onDeleteRow={deleteContentRow}
            onDeleteSection={deleteDisclosureSection}
          />
        )}
        {customSections.map((section) => (
          <DisclosureTable
            key={section.id}
            sectionId={`${CUSTOM_SECTION_PREFIX}${section.id}`}
            title={section.title}
            head={["S.NO", "INFORMATION", "DETAILS"]}
            rows={addDocumentUrls(section.rows, uploadedDocuments)}
            canManageDocuments={canManageDocuments}
            uploadingDocumentId={uploadingDocumentId}
            onDocumentUpload={handleDocumentUpload}
            canManageContent={canManageDocuments}
            onSaveRow={saveContentRow}
            onAddRow={addContentRow}
            statusMessage={statusMessageForSection(
              `${CUSTOM_SECTION_PREFIX}${section.id}`,
            )}
            onDeleteRow={deleteContentRow}
            onDeleteSection={deleteDisclosureSection}
          />
        ))}
      </div>
    </main>
  );
}
